import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Package, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[14px] outline-none focus:border-neutral-400';

const KitPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try {
      const { data } = await axios.get(`${API}/kits/medicine-cabinet`);
      setKit(data);
    } catch (e) {
      setMsg('Failed to load kit: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setKit(prev => ({ ...prev, [k]: v }));
  const updItem = (i, k, v) => setKit(prev => {
    const items = [...(prev.items || [])];
    items[i] = { ...items[i], [k]: v };
    return { ...prev, items };
  });
  const addItem = () => setKit(prev => ({ ...prev, items: [...(prev.items || []), { name: '', pack: '', desc: '' }] }));
  const rmItem = (i) => setKit(prev => ({ ...prev, items: (prev.items || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!kit) return;
    setSaving(true); setMsg('');
    try {
      const payload = {
        tag: kit.tag,
        title: kit.title,
        subtitle: kit.subtitle,
        price: Number(kit.price),
        image: kit.image,
        items: kit.items,
      };
      const { data } = await axios.patch(`${API}/admin/kits/medicine-cabinet`, payload, { headers });
      setKit(data);
      setMsg('Saved successfully. Customer catalog updated live.');
    } catch (e) {
      setMsg('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading || !kit) return <div className="text-neutral-500 text-[13.5px] py-10 text-center">Loading kit…</div>;

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Package size={20} className="text-neutral-400" /> Medicine Cabinet</h2>
          <p className="text-[12.5px] text-neutral-500">Edit the featured essentials kit shown on the homepage.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Reload</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Title</span><input data-testid="kit-title-input" className={inputCls} value={kit.title || ''} onChange={(e) => upd('title', e.target.value)} /></label>
        <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Price ($)</span><input data-testid="kit-price-input" type="number" step="0.01" className={inputCls} value={kit.price ?? ''} onChange={(e) => upd('price', e.target.value)} /></label>
        <label className="block md:col-span-2"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Subtitle / description</span><textarea data-testid="kit-subtitle-input" rows={2} className={inputCls} value={kit.subtitle || ''} onChange={(e) => upd('subtitle', e.target.value)} /></label>
        <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Tag</span><input className={inputCls} value={kit.tag || ''} onChange={(e) => upd('tag', e.target.value)} /></label>
        <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Image URL</span><input className={inputCls} value={kit.image || ''} onChange={(e) => upd('image', e.target.value)} placeholder="https://…" /></label>
      </div>

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
          <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100">Items in the kit ({(kit.items || []).length})</div>
          <button data-testid="kit-add-item-btn" onClick={addItem} className="inline-flex items-center gap-1 text-[12.5px] bg-neutral-900 text-white rounded-md px-3 py-1.5"><Plus size={13} /> Add item</button>
        </div>
        {(kit.items || []).length === 0 && <div className="p-4 text-neutral-500 text-[13px]">No items yet — click Add item.</div>}
        {(kit.items || []).map((it, i) => (
          <div key={i} data-testid={`kit-item-${i}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
            <input placeholder="Name (e.g. Azithromycin 500)" className={`col-span-4 ${inputCls} py-2`} value={it.name || ''} onChange={(e) => updItem(i, 'name', e.target.value)} />
            <input placeholder="Pack (e.g. 50 tabs)" className={`col-span-2 ${inputCls} py-2`} value={it.pack || ''} onChange={(e) => updItem(i, 'pack', e.target.value)} />
            <input placeholder="Short description" className={`col-span-5 ${inputCls} py-2`} value={it.desc || ''} onChange={(e) => updItem(i, 'desc', e.target.value)} />
            <button onClick={() => rmItem(i)} className="col-span-1 rounded-md text-neutral-500 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      {msg && <div className={`text-[13px] ${msg.startsWith('Saved') ? 'text-emerald-600' : 'text-red-600'}`}>{msg}</div>}

      <div className="flex items-center justify-end">
        <button data-testid="kit-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-5 py-2.5 text-[13.5px] disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save kit'}</button>
      </div>
    </div>
  );
};

export default KitPanel;
